import { type DriveFile } from '@/shared/DriveTypes';
import { useEffect, useRef, useState } from 'react';
import { useKeyPress } from '~/utils/hooks/hooks';
import { isAFolder } from '~/utils/utils';
import * as styles from './Tree.css';
import { useMutateFile } from './treeMenuUtils';

export const EditableTreeItem = ({
  file,
  files,
  wiki,
  children,
  linkRef,
}: {
  file: DriveFile;
  files: DriveFile[];
  wiki: DriveFile | undefined;
  children: string;
  linkRef?: React.RefObject<HTMLAnchorElement | null>;
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const mutateFile = useMutateFile({ file, files, wiki });
  const startEdit = () => {
    setEditValue(children);
    setIsEditing(true);
  };

  useEffect(() => {
    const link = linkRef?.current;
    if (!link) return;

    const handleDoubleClick = (e: MouseEvent) => {
      const target = e.currentTarget as HTMLElement;
      if (target.tagName !== link.nodeName) return;
      e.preventDefault();
      e.stopPropagation();
      setEditValue(children);
      setIsEditing(true);
    };

    link.addEventListener('dblclick', handleDoubleClick);
    return () => link.removeEventListener('dblclick', handleDoubleClick);
  }, [linkRef, children]);

  useKeyPress('Enter', () => {
    if (linkRef?.current) {
      startEdit();
    }
  });

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleDoubleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    startEdit();
  };

  const handleSave = () => {
    const trimmedValue = editValue.trim();
    if (trimmedValue && trimmedValue !== children) {
      void mutateFile({ name: trimmedValue });
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditValue('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  };
  const isFolder = isAFolder(file);
  const isEmptyFolder = isFolder && !files.some((f) => f.parents?.includes(file.id));

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        type="text"
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        className={styles.treeItemInput}
        style={{
          fontWeight: isFolder ? 'bold' : undefined,
          marginLeft: isEmptyFolder ? 2 : undefined,
        }}
      />
    );
  }

  return (
    <span className={styles.linkText} onDoubleClick={linkRef ? undefined : handleDoubleClick}>
      {children}
    </span>
  );
};
